import { Component, Input, Output, EventEmitter, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { UserInteractionApiService } from '@angular-workspace/services';
import { GoogleAnalyticsService } from '../../../services/google-analytics.service';
import { navigateToCatalogueAssetDetail } from '../../utils/catalogue-asset-detail-navigation.util';
import { CollectionListingStateService } from '../../services/collection-listing-state.service';
// Share feature: commented out for now. Uncomment to re-enable.
// import { CardShareButtonComponent } from '../card-share-button/card-share-button.component';

export interface FeatureCardData {
  id: string;
  title: string;
  shortDescription?: string; // Short description of the asset, shown below the label
  organization: string;
  industry?: string;
  tags: string[]; // Resource server name/type badges (shown under title with query types)
  /** Query/access modalities from resource servers (queryTypes only), e.g. ATTR, STAC */
  serverAccessTypes?: string[];
  apiTags?: string[]; // Actual tags from API — shown in Metadata / secondary areas
  percentage?: number;
  fileType?: string;
  date?: string;
  badge?: string;
  badgeColor?: 'public' | 'private' | 'restricted';
  isNew?: boolean;
  resourceCount?: number;
  link?: string;
  metrics?: {
    likes?: number;
    downloads?: number;
    files?: number;
    views?: number;
  };
}

@Component({
  selector: 'app-feature-card',
  standalone: true,
  imports: [CommonModule /* , CardShareButtonComponent */], // Share feature: uncomment to re-enable
  templateUrl: './feature-card.component.html',
  styleUrl: './feature-card.component.scss',
})
export class FeatureCardComponent {
  @Input() card!: FeatureCardData;
  /** When true, short description is shown below the label (e.g. in list view). Default false for grid/table. */
  @Input() showShortDescription: boolean = false;
  /** When true, activity metrics (likes, downloads, views) are shown. Set false e.g. on Features of MahaAgX landing. */
  @Input() showActivityMetrics: boolean = true;
  /** When true, show organization name prominently below the title (e.g. when Bookmarks view is active). */
  @Input() showOrganizationBelowTitle: boolean = false;
  /** When false, like count is display-only (e.g. dataset collection cards). */
  @Input() likeActionEnabled: boolean = true;
  @Input() isBookmarked: boolean = false;
  @Input() isLiked: boolean = false;
  @Input() isLoggedIn: boolean = false;
  @Output() bookmarkChanged = new EventEmitter<boolean>();
  @Output() likeChanged = new EventEmitter<boolean>();
  /** When true, navigating to detail preserves the parent collection page filters on return. */
  @Input() markCollectionRestore = false;

  private router = inject(Router);
  private userInteractionApiService = inject(UserInteractionApiService);
  private googleAnalytics = inject(GoogleAnalyticsService);
  private listingState = inject(CollectionListingStateService);

  getBadgeClass(): string {
    if (!this.card.badgeColor) return 'feature-card__badge--public';
    return `feature-card__badge--${this.card.badgeColor}`;
  }

  onCardClick(): void {
    // Navigate to dataset detail page if id is available
    if (this.card?.id) {
      console.log('Navigating to dataset detail page with ID:', this.card.id);
      this.googleAnalytics.trackDatasetClick(this.card.title ?? '', this.card.id);
      if (this.markCollectionRestore) {
        this.listingState.markPendingRestore('datasets');
      }
      void navigateToCatalogueAssetDetail(this.router, 'dataset', this.card.id);
    } else if (this.card?.link) {
      console.log('Navigating to link:', this.card.link);
      this.router.navigateByUrl(this.card.link);
    } else {
      console.warn('No ID or link available for navigation. Card data:', this.card);
    }
  }

  getDisplayTag(tag: string): string {
    // Replace PII with Personal for UI display only (case-insensitive)
    if (!tag) return tag;
    return tag.toUpperCase() === 'PII' ? 'Personal' : tag;
  }

  /**
   * Handle bookmark icon click in metrics section
   */
  async onBookmarkIconClick(event: Event): Promise<void> {
    event.stopPropagation(); // Prevent card click navigation
    
    if (!this.isLoggedIn) {
      // Emit event to show login modal (handled by parent)
      return;
    }

    if (!this.card?.id) {
      console.error('Cannot bookmark: card ID is missing');
      return;
    }

    try {
      const actionType = this.isBookmarked ? 'UNBOOKMARK' : 'BOOKMARK';
      
      const payload = {
        assetId: this.card.id,
        assetType: 'DATABANK',
        actionType: actionType
      };

      await this.userInteractionApiService.createBookmarkOrLike(payload);
      
      // Update local state
      this.isBookmarked = !this.isBookmarked;
      this.bookmarkChanged.emit(this.isBookmarked);
    } catch (error) {
      console.error('Error toggling bookmark:', error);
    }
  }

  /**
   * Handle like icon click in metrics section
   */
  onLikeMetricClick(event: Event): void {
    event.stopPropagation();
    if (!this.likeActionEnabled) {
      return;
    }
    void this.onLikeIconClick(event);
  }

  async onLikeIconClick(event: Event): Promise<void> {
    event.stopPropagation(); // Prevent card click navigation
    
    if (!this.isLoggedIn) {
      // Emit event to show login modal (handled by parent)
      return;
    }

    if (!this.card?.id) {
      console.error('Cannot like: card ID is missing');
      return;
    }

    try {
      const actionType = this.isLiked ? 'DISLIKE' : 'LIKE';
      
      const payload = {
        assetId: this.card.id,
        assetType: 'DATABANK',
        actionType: actionType
      };

      await this.userInteractionApiService.createBookmarkOrLike(payload);
      
      // Update local state
      this.isLiked = !this.isLiked;
      this.likeChanged.emit(this.isLiked);
      
      // Update metrics count
      if (this.card.metrics) {
        if (this.isLiked) {
          this.card.metrics.likes = (this.card.metrics.likes || 0) + 1;
        } else {
          this.card.metrics.likes = Math.max(0, (this.card.metrics.likes || 0) - 1);
        }
      }
    } catch (error) {
      console.error('Error toggling like:', error);
    }
  }
}



